import { FC } from 'react';

import {
    SemiDetachedHouseMedium,
    DetachedHouseMedium,
    TerracedHouseMedium,
    FlatsMedium,
    BungalowMedium
} from '@zoopla-eng/zds/icons';

import { Legend } from './legend';
import { Box } from '@zoopla-eng/zds/box';

export type PropertyTypeProps = {
    type: PropertyListingTypes;
};

const PROPERTY_LISTING_TYPES = [
    'Detached House',
    'Detached Property',
    'Semi-Detached House',
    'Semi-Detached Property',
    'End Terrace House',
    'End Terrace Property',
    'Mid Terrace House',
    'Mid Terrace Property',
    'Terrace Property',
    'Terraced',
    'Converted Flat',
    'Flat/Maisonette',
    'Purpose Built Flat',
    'Bungalow Property',
    'Detached Bungalow',
    'End Terrace Bungalow',
    'Mid Terrace Bungalow',
    'Semi-Detached Bungalow',
    'Terraced Bungalow',
    'barn_conversion',
    'cottage',
    'country_house',
    'detached',
    'farmhouse',
    'link_detached',
    'lodge',
    'semi_detached',
    'end_terrace',
    'mews',
    'terraced',
    'town_house',
    'flat',
    'maisonette',
    'studio',
    'detached_bungalow',
    'semi_detached_bungalow',
    'terraced_bungalow',
    'bungalow'
] as const;

export type PropertyListingTypes = typeof PROPERTY_LISTING_TYPES[number];

type PropertyTypeDisplayText =
    | 'Detached'
    | 'Semi-detached'
    | 'End Terrace'
    | 'Mid Terrace'
    | 'Terrace'
    | 'Converted Flat'
    | 'Flat/Apartment'
    | 'Detached Bungalow'
    | 'End Terrace Bungalow'
    | 'Mid Terrace Bungalow'
    | 'Terraced Bungalow'
    | 'Barn conversion'
    | 'Cottage'
    | 'Country House'
    | 'Farmhouse'
    | 'Lodge'
    | 'Mews House'
    | 'Town House'
    | 'Maisonette'
    | 'Studio'
    | 'Semi-detached Bungalow'
    | 'Bungalow';

type IconMap = {
    [key in PropertyListingTypes]: [PropertyTypeDisplayText, JSX.Element];
};

const iconMap: IconMap = {
    // Recently sold properties
    'Bungalow Property': ['Bungalow', <BungalowMedium />],
    'Converted Flat': ['Converted Flat', <FlatsMedium />],
    'Detached Bungalow': ['Detached Bungalow', <DetachedHouseMedium />],
    'Detached House': ['Detached', <DetachedHouseMedium />],
    'Detached Property': ['Detached', <DetachedHouseMedium />],
    'End Terrace Bungalow': ['End Terrace Bungalow', <BungalowMedium />],
    'End Terrace House': ['End Terrace', <SemiDetachedHouseMedium />],
    'End Terrace Property': ['End Terrace', <SemiDetachedHouseMedium />],
    'Flat/Maisonette': ['Flat/Apartment', <FlatsMedium />],
    'Mid Terrace Bungalow': ['Mid Terrace Bungalow', <BungalowMedium />],
    'Mid Terrace House': ['Mid Terrace', <TerracedHouseMedium />],
    'Mid Terrace Property': ['Mid Terrace', <TerracedHouseMedium />],
    'Purpose Built Flat': ['Flat/Apartment', <FlatsMedium />],
    'Semi-Detached Bungalow': ['Semi-detached Bungalow', <BungalowMedium />],
    'Semi-Detached House': ['Semi-detached', <SemiDetachedHouseMedium />],
    'Semi-Detached Property': ['Semi-detached', <SemiDetachedHouseMedium />],
    'Terrace Property': ['Terrace', <TerracedHouseMedium />],
    'Terraced Bungalow': ['Terraced Bungalow', <BungalowMedium />],
    Terraced: ['Terrace', <TerracedHouseMedium />],
    // Listings
    barn_conversion: ['Barn conversion', <DetachedHouseMedium />],
    bungalow: ['Bungalow', <BungalowMedium />],
    cottage: ['Cottage', <DetachedHouseMedium />],
    country_house: ['Country House', <DetachedHouseMedium />],
    detached_bungalow: ['Detached Bungalow', <BungalowMedium />],
    detached: ['Detached', <DetachedHouseMedium />],
    end_terrace: ['End Terrace', <SemiDetachedHouseMedium />],
    farmhouse: ['Farmhouse', <DetachedHouseMedium />],
    flat: ['Flat/Apartment', <FlatsMedium />],
    link_detached: ['Detached', <DetachedHouseMedium />],
    lodge: ['Lodge', <DetachedHouseMedium />],
    maisonette: ['Maisonette', <FlatsMedium />],
    mews: ['Mews House', <TerracedHouseMedium />],
    semi_detached_bungalow: ['Semi-detached Bungalow', <BungalowMedium />],
    semi_detached: ['Semi-detached', <SemiDetachedHouseMedium />],
    studio: ['Studio', <FlatsMedium />],
    terraced_bungalow: ['Terraced Bungalow', <BungalowMedium />],
    terraced: ['Terrace', <TerracedHouseMedium />],
    town_house: ['Town House', <TerracedHouseMedium />]
};

export const PropertyType: FC<PropertyTypeProps> = ({ type }) => {
    const [typeText, icon] = iconMap[type] || [];

    if (!typeText || !icon) return null;

    return (
        <Legend>
            <Box display="flex" aria-hidden="true">
                {icon}
            </Box>
            <div>{typeText}</div>
        </Legend>
    );
};

export const isPropertyListingType = (
    type: unknown
): type is PropertyListingTypes => {
    return (
        typeof type === 'string' &&
        PROPERTY_LISTING_TYPES.includes(type as PropertyListingTypes)
    );
};
